import React, { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, LogOut, CheckCircle, Circle, Trophy, Lock } from 'lucide-react';

interface HeaderProps {
  currentPage: string;
  user: {
    role: 'paciente' | 'nutricionista';
    name: string;
    email: string;
    avatar?: string;
  };
  onLogout: () => void;
  onPageChange: (page: string) => void;
  onAlterarSenha?: () => void;
}

const Header: React.FC<HeaderProps> = ({ currentPage, user, onLogout, onPageChange, onAlterarSenha }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getStepStatus = (step: string) => {
    return 'completed';
  };

  return (
    <header className="header bg-white border-bottom shadow-sm p-3">
      <div className="container-fluid">
        <div className="d-flex align-items-center w-100">
          {user.role === 'nutricionista' && (
            <div className="progress-trail-container flex-grow-1 me-4">
              <div className="progress-trail d-flex align-items-center justify-content-between position-relative">
                <div className="progress-line"></div>
                
                <div className={`step-item ${getStepStatus('agendamento')}`}>
                  <div className="step-circle">
                    {getStepStatus('agendamento') === 'completed' ? 
                      <CheckCircle size={24} /> : 
                      <Circle size={24} />
                    }
                  </div>
                  <div className="step-label">
                    <span className="step-title">Agendamento</span>
                  </div>
                </div>
                
                <div className={`step-item ${getStepStatus('consulta')}`}>
                  <div className="step-circle">
                    {getStepStatus('consulta') === 'completed' ? 
                      <CheckCircle size={24} /> : 
                      <Circle size={24} />
                    }
                  </div>
                  <div className="step-label">
                    <span className="step-title">Consulta</span>
                  </div>
                </div>
                
                <div className="step-item completed">
                  <div className="step-circle trophy-style">
                    <Trophy size={24} />
                  </div>
                  <div className="step-label">
                    <span className="step-title trophy-text">Sucesso!</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex-grow-1 d-flex justify-content-center d-md-none">
            <img 
              src="/logo3.png" 
              alt="Logo" 
              className="mobile-logo"
              style={{height: '95px', width: 'auto', objectFit: 'contain'}}
            />
          </div>
          
          <div className="ms-auto">
            <div className="position-relative" ref={dropdownRef}>
              <button 
                type="button"
                className="user-card d-flex align-items-center gap-2 bg-light p-2 rounded border"
                onClick={() => setShowDropdown(!showDropdown)}
                style={{cursor: 'pointer', border: 'none'}}
              >
                <div className="user-avatar d-flex align-items-center justify-content-center bg-success text-white rounded-circle" style={{width: '32px', height: '32px'}}>
                  {user.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt={user.name}
                      className="rounded-circle"
                      style={{width: '32px', height: '32px', objectFit: 'cover'}}
                    />
                  ) : (
                    <User size={18} />
                  )}
                </div>
                <div className="user-info">
                  <div className="user-name fw-semibold text-dark" style={{fontSize: '0.8rem'}}>{user.name}</div>
                  <div className="user-email text-muted" style={{fontSize: '0.7rem'}}>{user.email}</div>
                </div>
                <ChevronDown 
                  size={16} 
                  className="text-muted" 
                  style={{
                    transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </button>
              
              {showDropdown && (
                <div className="dropdown-menu show position-absolute end-0 mt-1 shadow border" style={{minWidth: '160px', zIndex: 1050}}>
                  <button 
                    className="dropdown-item d-flex align-items-center gap-2 py-2 px-3 text-start"
                    style={{fontSize: '0.8rem'}}
                    onClick={() => {
                      setShowDropdown(false);
                      onPageChange('meus-dados');
                    }}
                  >
                    <User size={14} />
                    Meus Dados
                  </button>
                  <button 
                    className="dropdown-item d-flex align-items-center gap-2 py-2 px-3 text-start"
                    style={{fontSize: '0.8rem'}}
                    onClick={() => {
                      setShowDropdown(false);
                      onAlterarSenha?.();
                    }}
                  >
                    <Lock size={14} />
                    Alterar Senha
                  </button>
                  <button 
                    className="dropdown-item d-flex align-items-center gap-2 py-2 px-3 text-start"
                    style={{fontSize: '0.8rem'}}
                    onClick={() => {
                      setShowDropdown(false);
                      onLogout();
                    }}
                  >
                    <LogOut size={14} />
                    Sair
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;